import { FC, ReactNode, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Box,
  Typography,
  TextField,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  Grid,
  Autocomplete,
  CircularProgress,
  Divider,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ptBR } from "date-fns/locale";
import {
  format,
  isBefore,
  isAfter,
  isEqual,
  parseISO,
  parse,
  startOfDay,
  isValid,
} from "date-fns";

import { useTranslation } from "react-i18next";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { EnumForeignDocumentType, EnumNationality } from "../types/enumsForms";
import {
  booleanOptions,
  foreignDocumentTypeOptions,
  nationalityOptions,
  officeOptions,
  ufOptions,
} from "../utils/optionsSelectForms";
import { useCreateWorker } from "services/input-core/workers/set-workers-acelen";
import { toast } from "react-toastify";
import { useSowWorkerRoles } from "services/input-core/sows/get-sow-worker-roles";
import { checkMessageErrors, mapStateToUF } from "utils/functions";
import { useSOWsQuery } from "services/input-core/sows/get-all-sows";
import { useSubscription } from "store/auth/auth-store";
import InputMask, { Props } from "react-input-mask";
import { useGetPostalCode } from "services/input-core/postalCode/postalCode";
import { JSX } from "react/jsx-runtime";
import { useGetPickList } from "services/input-core/pick-lists/get-ticket-list";

type NewWorkerNames =
  | "nationality"
  | "firstName"
  | "lastName"
  | "birthDate"
  | "motherName"
  | "email"
  | "cep"
  | "address"
  | "addressNumber"
  | "city"
  | "uf"
  | "state"
  | "workerType"
  | "routineStopWorker"
  | "temporaryWorker"
  | "startDate"
  | "endDate"
  | "unitOfMeasure"
  | "hoursPerDay"
  | "hoursPerWeek"
  | "rateCategoryPr"
  | "rateCategoryBr"
  | "siteCode"
  | "location"
  | "sowWorkerRole"
  | "sowId"
  | "workerCountryOrigin"
  | "generalRegister"
  | "comment"
  | "cpf"
  | "passport"
  | "rne"
  | "type_of_foreign_document"
  // Campos comentados segundo pedido da acelen para a determinada sprint-60
  | "subcontractorCompany"
  | "workShift"
  | "phone"
  | "department"
  | "subcontractor";
type CustomFormatChars = {
  [char: string]: string;
};

type TInputMaskCorrect = Omit<Props, "children"> & {
  children?: () => JSX.Element;
  maskChar?: string | null;
};

const InputMaskCorrect: FC<TInputMaskCorrect> = ({ children, ...props }) => {
  const child = (children as unknown) as ReactNode;

  const formatChars: CustomFormatChars = {
    A: "[A-Z]",
    N: "[A-Z0-9]",
    "9": "[0-9]",
    a: "[A-Za-z]",
    "*": "[A-Za-z0-9]",
  };

  return (
    <InputMask {...(props as any)} formatChars={formatChars} children={child} />
  );
};

export const FormCreateWorker = () => {
  const {
    mutate: fetchPostalCode,
    isLoading: isFetchingPostalCode,
  } = useGetPostalCode();

  const { t } = useTranslation([
    "form-create-worker",
    "list-messages",
    "list-errors",
  ]);

  const newWorkerSchema = z
    .object({
      // Documentos de trabalhador
      nationality: z
        .string({
          required_error: t("required-selection-error"),
        })
        .min(3, t("nationality-error")),
      type_of_foreign_document: z.string({
        required_error: t("required-selection-error"),
      }),
      cpf: z.string({
        required_error: t("field-required"),
      }),
      rne: z.string({
        required_error: t("field-required"),
      }),
      passport: z.string({
        required_error: t("field-required"),
      }),
      generalRegister: z.string({
        required_error: t("field-required"),
      }),

      // Dados do trabalhador
      firstName: z
        .string({
          required_error: t("field-required"),
        })
        .min(3, t("name-length-error"))
        .regex(/^[a-záàâãäéèêëíïóôõöúüçñ -']+$/i, t("letters-only-error")),
      lastName: z
        .string({
          required_error: t("field-required"),
        })
        .min(3, t("name-length-error"))
        .regex(/^[a-záàâãäéèêëíïóôõöúüçñ -']+$/i, t("letters-only-error")),
      email: z
        .string({
          required_error: t("field-required"),
        })
        .regex(
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
          t("invalid-email-error"),
        ),
      birthDate: z.string({
        required_error: t("field-required"),
      }),
      motherName: z.string({
        required_error: t("field-required"),
      }),

      // Dados de endereço
      cep: z
        .string({
          required_error: t("field-required"),
        })
        .regex(/^[0-9]{5}-[0-9]{2}/i, t("valid-cep-error")),
      address: z.string({
        required_error: t("field-required"),
      }),
      addressNumber: z.string({
        required_error: t("field-required"),
      }),
      city: z.string({
        required_error: t("field-required"),
      }),
      state: z
        .string({
          required_error: t("required-selection-error"),
        })
        .min(2, t("required-selection-error")),
      uf: z.string().default("NA"),

      // Dados de trabalho
      unitOfMeasure: z.string().default("Hour"),
      hoursPerDay: z.string().default("8"),
      hoursPerWeek: z.string().default("40"),
      rateCategoryBr: z.string().default("40.00"),
      rateCategoryPr: z.string().default("ST"),

      // Tipo de trabalhador
      startDate: z.string({
        required_error: t("field-required"),
      }),
      endDate: z.string({
        required_error: t("field-required"),
      }),

      workerType: z.string().default("sow worker"),

      temporaryWorker: z
        .string({
          required_error: t("required-selection-error"),
        })
        .min(2, t("worker-type-error")),
      routineStopWorker: z
        .string({ required_error: t("required-selection-error") })
        .min(2, t("worker-type-error")),

      // Dados complementares de sow/worker
      workerCountryOrigin: z.string().default("NA"),

      sowId: z
        .string({
          required_error: t("required-selection-error"),
        })
        .regex(/[A-Z\d]/g, t("valid-code-error")),
      sowWorkerRole: z
        .string({ required_error: t("required-selection-error") })
        .min(3, t("code-selection-error")),
      siteCode: z
        .string({
          required_error: t("required-selection-error"),
        })
        .min(3, t("code-selection-error")),
      location: z
        .string({
          required_error: t("required-selection-error"),
        })
        .min(3, t("code-selection-error")),
      comment: z.string().optional(),
      sowStartDate: z.date().optional(),
      sowEndDate: z.date().optional(),

      // Campos comentados segundo pedido da acelen para a determinada sprint-60
      subcontractorCompany: z.string().optional(),
      workShift: z
        .string({ required_error: t("required-selection-error") })
        .min(2, t("worker-type-error")),
      phone: z
        .string({
          required_error: t("field-required"),
        })
        .regex(/^\(?\d{2}\)?\s?\d{4,5}-?\d{4}$/, t("invalid-phone-error")),
      department: z
        .string({
          required_error: t("required-selection-error"),
        })
        .min(3, t("code-selection-error")),
      subcontractor: z
        .string({
          required_error: t("required-selection-error"),
        })
        .min(2, t("code-selection-error")),
    })
    .superRefine((fields, ctx) => {
      if (
        (fields.nationality === EnumNationality.BR ||
          (fields.nationality === EnumNationality.GRINGO &&
            fields.type_of_foreign_document === EnumForeignDocumentType.RNE)) &&
        !/^\d{3}\.\d{3}\.\d{3}-\d{2}$/g.test(fields.cpf) &&
        fields.cpf.length <= 14
      ) {
        ctx.addIssue({
          path: ["cpf"],
          code: z.ZodIssueCode.invalid_string,
          validation: "regex",
          message: t("valid-document-error"),
        });
      }

      if (
        fields.nationality === EnumNationality.BR &&
        (fields.generalRegister.length < 7 ||
          fields.generalRegister.length > 20) &&
        fields.generalRegister.length >= 0
      ) {
        ctx.addIssue({
          path: ["generalRegister"],
          code: z.ZodIssueCode.invalid_string,
          validation: "regex",
          message: t("valid-document-error"),
        });
      }

      if (
        fields.nationality === EnumNationality.GRINGO &&
        fields.type_of_foreign_document === EnumForeignDocumentType.RNE &&
        !/^([A-Z\d]{8,11})$/.test(fields.rne)
      ) {
        ctx.addIssue({
          path: ["rne"],
          code: z.ZodIssueCode.invalid_string,
          validation: "regex",
          message: t("valid-document-error"),
        });
      }

      if (
        fields.nationality === EnumNationality.GRINGO &&
        fields.type_of_foreign_document === EnumForeignDocumentType.PASSPORT &&
        (fields.passport.length < 5 || fields.passport.length > 20)
      ) {
        ctx.addIssue({
          path: ["passport"],
          code: z.ZodIssueCode.invalid_string,
          validation: "regex",
          message: t("valid-document-error"),
        });
      }

      if (
        fields.nationality === EnumNationality.GRINGO &&
        fields.type_of_foreign_document.length < 3
      ) {
        ctx.addIssue({
          path: ["type_of_foreign_document"],
          code: z.ZodIssueCode.custom,
          fatal: false,
          message: t("document-type-selection-error"),
        });
      }

      if (
        isAfter(
          startOfDay(parseISO(fields.startDate)),
          startOfDay(parseISO(fields.endDate)),
        ) ||
        isEqual(
          startOfDay(parseISO(fields.startDate)),
          startOfDay(parseISO(fields.endDate)),
        )
      ) {
        ctx.addIssue({
          path: ["endDate"],
          code: z.ZodIssueCode.invalid_date,
          message: t("start-date-greater-than-end-date"),
        });
      }

      if (
        isBefore(
          startOfDay(parseISO(fields.startDate)),
          startOfDay(fields.sowStartDate),
        )
      ) {
        ctx.addIssue({
          path: ["startDate"],
          code: z.ZodIssueCode.invalid_date,
          message: t("start-dates-not-between-dates-from-sow"),
        });
      }
      // Campos comentados segundo pedido da acelen para a determinada sprint-60
      if (fields.subcontractor === "Yes" && !fields.subcontractorCompany) {
        ctx.addIssue({
          path: ["subcontractorCompany"],
          code: z.ZodIssueCode.invalid_string,
          validation: "regex",
          message: t("field-required"),
        });
      }
    })
    .transform((fields) => ({
      nationality: fields.nationality,
      cpf:
        fields.nationality === EnumNationality.BR ||
        (fields.nationality === EnumNationality.GRINGO &&
          fields.type_of_foreign_document === EnumForeignDocumentType.RNE)
          ? fields.cpf
          : "",
      type_of_foreign_document:
        fields.nationality === EnumNationality.GRINGO
          ? fields.type_of_foreign_document
          : "",
      rne:
        fields.type_of_foreign_document == EnumForeignDocumentType.RNE
          ? fields.rne
          : "",
      passport:
        fields.type_of_foreign_document == EnumForeignDocumentType.PASSPORT
          ? fields.passport
          : "",
      firstName: fields.firstName,
      lastName: fields.lastName,
      birthDate: fields.birthDate.toString(),
      motherName: fields.motherName,
      email: fields.email,
      cep: fields.cep ?? "",
      address: fields.address,
      addressNumber: fields.addressNumber,
      city: fields.city,
      uf: fields.uf,
      state: fields.state,
      workerType: fields.workerType || "sow worker",
      routineStopWorker: fields.routineStopWorker,
      temporaryWorker: fields.temporaryWorker,

      startDate: fields.startDate.toString(),
      endDate: fields.endDate.toString(),
      unitOfMeasure: fields.unitOfMeasure,
      hoursPerDay: fields.hoursPerDay,
      hoursPerWeek: fields.hoursPerWeek,
      rateCategoryPr: fields.rateCategoryPr,
      rateCategoryBr: fields.rateCategoryBr,

      siteCode: fields.siteCode,
      location: fields.location,
      sowWorkerRole: fields.sowWorkerRole,
      sowId: fields.sowId,
      sowStartDate: fields.sowStartDate,
      sowEndDate: fields.sowEndDate,
      workerCountryOrigin: fields.workerCountryOrigin,
      generalRegister:
        fields.nationality === EnumNationality.BR
          ? fields.generalRegister
          : "NA",
      comment: fields.comment,

      // Campos comentados segundo pedido da acelen para a determinada sprint-60
      subcontractorCompany: fields.subcontractorCompany,
      subcontractor: fields.subcontractor,
      phone: fields.phone,
      department: fields.department,
      workShift: fields.workShift,
    }));

  type NewWorkerFormFields = z.infer<typeof newWorkerSchema>;

  const {
    handleSubmit,
    register,
    watch,
    setValue,
    reset,
    control,
    formState: { errors },
  } = useForm<NewWorkerFormFields>({
    mode: "all",
    criteriaMode: "all",
    resolver: zodResolver(newWorkerSchema),
  });

  useEffect(() => {
    setValue("type_of_foreign_document", "");
    setValue("rne", "");
    setValue("cpf", "");
    setValue("passport", "");
    setValue("generalRegister", "");
  }, [watch("nationality")]);

  useEffect(() => {
    setValue("rne", "");
    setValue("passport", "");
    setValue("cpf", "");
  }, [watch("type_of_foreign_document")]);

  const {
    mutate: setCreateWorker,
    isLoading: isCreateWorker,
  } = useCreateWorker();

  const {
    data: sowWorkerRoles,
    isLoading: isSowWorkerRoles,
  } = useSowWorkerRoles("worker");

  const { subscription } = useSubscription();

  const { data: Sows, isLoading: isSowsLoading } = useSOWsQuery(
    subscription?.subscriptionId,
    {
      enabled: !!subscription?.subscriptionId,
    },
  );

  // Campos comentados segundo pedido da acelen para a determinada sprint-60
  const {
    data: dataPickListDepartamento,
    isLoading: isLoadingPickListDepartamento,
  } = useGetPickList("Departamento");

  const {
    data: dataPickListTurno,
    isLoading: isLoadingPickListTurno,
  } = useGetPickList("Turno");

  {
    /* sprint-60 */
  }

  const {
    data: dataPickListWorkShift,
    isLoading: isLoadingPickListWorkShift,
  } = useGetPickList("Trabalhador de Parada ou Rotina");

  useEffect(() => {
    const cep = watch("cep");

    if (cep?.length === 9 && !isCreateWorker) {
      fetchPostalCode(cep, {
        onSuccess: (data) => {
          setValue("address", data.street);
          setValue("city", data.city);
          setValue("state", mapStateToUF(data.state));
        },
        onError: () => {
          toast.error(
            t("postal-code-not-found", {
              ns: "form-create-worker",
            }),
          );
        },
      });
    }
  }, [watch("cep")]);

  const renderTextField = (
    name: NewWorkerNames,
    label: string,
    required = true,
    disabled = false,
    type = "text",
    multiline = false,
    rows = 1,
    maxRows = 1,
    mask?: string | (string | RegExp)[],
    loading = false,
  ) => (
    <Box display="flex" flexDirection="column" width="100%">
      {mask ? (
        <Controller
          data-cy={`controller-${name}`}
          name={name}
          control={control}
          defaultValue=""
          render={({ field }) => (
            <InputMaskCorrect
              {...field}
              mask={mask}
              maskChar=""
              maskPlaceholder=""
              alwaysShowMask={false} // Adicionando esta propriedade
              value={field.value}
              onChange={(e) => {
                field.onChange(e);
                setValue(
                  name,
                  name === "rne"
                    ? e.target.value.toUpperCase()
                    : e.target.value,
                ); // Converte o valor para maiúsculas se for no RNE
              }}
              disabled={disabled}
            >
              {() => (
                <TextField
                  data-cy={`input-${name}`}
                  id={name}
                  variant="outlined"
                  label={t(label)}
                  required={required}
                  type={type}
                  style={{ width: "100%" }}
                  multiline={multiline}
                  rows={rows}
                  maxRows={maxRows}
                  InputProps={{
                    endAdornment: loading ? (
                      <CircularProgress size={20} />
                    ) : null,
                  }}
                />
              )}
            </InputMaskCorrect>
          )}
        />
      ) : (
        <Controller
          data-cy={`controller-${name}`}
          name={name}
          control={control}
          defaultValue=""
          render={({ field }) => (
            <TextField
              {...field}
              data-cy={`input-${name}`}
              id={name}
              variant="outlined"
              label={t(label)}
              required={required}
              type={type}
              style={{ width: "100%" }}
              multiline={multiline}
              rows={rows}
              defaultValue=""
              maxRows={maxRows}
              disabled={disabled}
              InputProps={{
                endAdornment: loading ? <CircularProgress size={20} /> : null,
                sx: {
                  background: disabled ? "rgba(0, 0, 0, 0.036)" : "initial",
                },
              }}
            />
          )}
        />
      )}
      {errors[name] && (
        <Typography color="error" variant="caption" mt={1}>
          {errors[name]?.message}
        </Typography>
      )}
    </Box>
  );

  const renderDatePicker = (
    name: NewWorkerNames,
    label: string,
    required: boolean,
    minDate?: Date,
    maxDate?: Date,
    disabled?: boolean,
  ) => (
    <Box display="flex" flexDirection="column" width="100%">
      <Controller
        name={name}
        control={control}
        rules={{ required }}
        render={({ field, fieldState: { error } }) => (
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={ptBR}
          >
            <DatePicker
              {...field}
              data-cy={`date-picker-${name}`}
              label={t(label)}
              disabled={disabled}
              defaultValue={null}
              minDate={minDate}
              maxDate={maxDate}
              shouldDisableDate={(day) => minDate && isBefore(day, minDate)}
              onChange={(date) => {
                let dateString = "";
                if (date) {
                  if (isValid(date)) {
                    dateString = format(date, "yyyy-MM-dd");
                  }
                }
                field.onChange(dateString);
                setValue(name, dateString);
              }}
              value={
                Array.isArray(field.value) && field.value.length > 0
                  ? parseISO(field.value[0]) // Parse ISO string
                  : field.value
                  ? parseISO(field.value as string)
                  : null
              }
              slotProps={{
                textField: {
                  required: required,
                  error: Boolean(errors[name]),
                  helperText: errors[name]?.message,
                },
              }}
            />
          </LocalizationProvider>
        )}
      />
    </Box>
  );

  const renderAutocompleteField = (
    name: NewWorkerNames,
    label: string,
    options: { value: string | string; label: string }[],
    isMultiple = false,
    disabled = false,
    isLoading = false,
  ) => (
    <Box display="flex" flexDirection="column" width="100%">
      <Controller
        name={name}
        data-cy={`controller-${name}`}
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <Autocomplete
            {...field}
            data-cy={`autocomplete-${name}`}
            id={name}
            key={field?.value?.toString() || name}
            limitTags={4}
            multiple={isMultiple}
            options={options}
            getOptionLabel={(option: { value: string; label: string }) =>
              option?.label
            }
            disabled={isLoading || disabled}
            renderInput={(params) => (
              <TextField
                {...params}
                data-cy={`input-${name}`}
                label={t(label)}
                error={Boolean(errors[name])}
                helperText={errors[name]?.message}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoading ? (
                        <CircularProgress
                          size={20}
                          sx={{ marginRight: "1rem" }}
                        />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            onChange={(_, newValue: any) => {
              if (!newValue) {
                setValue(name, undefined);
              } else {
                field.onChange(newValue.value);
                setValue(name, newValue.value);
                if (name === "sowId") {
                  setValue(
                    "sowStartDate",
                    newValue.startDate
                      ? parse(newValue.startDate, "dd/MM/yyyy", new Date()) // Usa parse para converter para uma data válida
                      : undefined,
                  );
                  setValue(
                    "sowEndDate",
                    newValue.endDate
                      ? parse(newValue.endDate, "dd/MM/yyyy", new Date()) // Usa parse para converter para uma data válida
                      : undefined,
                  );
                }
              }
            }}
            value={
              options?.find((option) => option.value === field.value) || null
            }
          />
        )}
      />
    </Box>
  );

  const renderToggleButtonGroup = (
    name: NewWorkerNames,
    label: string,
    options: any[],
  ) => (
    <Box display="flex" flexDirection="column" mb={2}>
      <Typography variant="subtitle2" mb={1}>
        {t(label)}
      </Typography>
      <ToggleButtonGroup
        data-cy={`buttons-group-${name}`}
        color="primary"
        value={watch(name) || ""}
        exclusive
        name={name}
        {...register(name, { required: true })}
        onChange={(e: any) => {
          setValue(name, e.target.value, {
            shouldValidate: true,
          });
        }}
      >
        {options?.map((option: any) => (
          <ToggleButton
            data-cy={`button-${name}`}
            key={`${name}-${option.value}`}
            value={option.value}
            sx={{ width: "100%", padding: "15.5px 14px" }}
          >
            {t(option.label)}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      {errors[name] && (
        <Typography color="error" variant="caption" mt={1}>
          {errors[name]?.message}
        </Typography>
      )}
    </Box>
  );

  const onSubmit = async (data: NewWorkerFormFields) => {
    toast.info(
      t("info-create-worker", {
        ns: "list-messages",
      }),
    );

    data.workerType = "sow worker";

    delete data.sowStartDate;
    delete data.sowEndDate;

    setCreateWorker(
      {
        ...data,
      },
      {
        onSuccess: () => {
          toast.success(
            t("success-create-worker", {
              ns: "list-messages",
            }),
          );
          reset();
          setValue("temporaryWorker", "");
          setValue("routineStopWorker", "");
          setValue("workShift", "");
          setValue("subcontractor", "");
          setValue("comment", "");
          setValue("cep", "");
        },
        onError: (error: any) => {
          toast.error(
            t(
              checkMessageErrors(
                error?.response?.data?.error,
                "ERR_CREATE_WORKER",
              ),
              { ns: "list-errors" },
            ),
          );
        },
      },
    );
  };

  return (
    <Grid
      container
      justifyContent="center"
      alignItems="center"
      sm={12}
      lg={12}
      md={12}
    >
      <Box sx={{ width: "90%", maxWidth: "60vw", p: 4 }}>
        <form onSubmit={handleSubmit(onSubmit)} data-cy="create-worker-form">
          {isSowsLoading ||
          isSowWorkerRoles ||
          // Campos comentados segundo pedido da acelen para a determinada sprint-60
          isLoadingPickListTurno ||
          isLoadingPickListWorkShift ? (
            <CircularProgress />
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h4" color="rgba(0,0,0,0.75)">
                  {t("header-form-personal-data")}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>

              <Grid item xs={12}>
                {renderAutocompleteField(
                  "nationality",
                  "select-place-of-birth",
                  nationalityOptions,
                )}
              </Grid>

              {watch("nationality") === EnumNationality.BR && (
                <>
                  <Grid item xs={6}>
                    {renderTextField(
                      "cpf",
                      "cpf",
                      true,
                      false,
                      "text",
                      undefined,
                      undefined,
                      undefined,
                      "999.999.999-99",
                    )}
                  </Grid>
                  <Grid item xs={6}>
                    {renderTextField(
                      "generalRegister",
                      "general-register",
                      true,
                      false,
                      "text",
                      undefined,
                      undefined,
                      undefined,
                      "**********************",
                    )}
                  </Grid>
                </>
              )}

              {watch("nationality") === EnumNationality.GRINGO && (
                <>
                  <Grid item xs={6}>
                    {renderAutocompleteField(
                      "type_of_foreign_document",
                      "select-document-type",
                      foreignDocumentTypeOptions,
                    )}
                  </Grid>
                  <Grid item xs={6}>
                    {watch("type_of_foreign_document") ===
                    EnumForeignDocumentType.RNE
                      ? renderTextField(
                          "rne",
                          "rne",
                          true,
                          false,
                          "text",
                          undefined,
                          undefined,
                          undefined,
                          "***********",
                        )
                      : watch("type_of_foreign_document") ===
                          EnumForeignDocumentType.PASSPORT &&
                        renderTextField(
                          "passport",
                          "passport",
                          true,
                          false,
                          "text",
                          undefined,
                          undefined,
                          undefined,
                          "********************",
                        )}
                  </Grid>

                  {watch("type_of_foreign_document") ===
                    EnumForeignDocumentType.RNE && (
                    <>
                      <Grid item xs={6}>
                        {renderTextField(
                          "cpf",
                          "cpf",
                          true,
                          false,
                          "text",
                          undefined,
                          undefined,
                          undefined,
                          "999.999.999-99",
                        )}
                      </Grid>
                      <Grid item xs={6}></Grid>
                    </>
                  )}
                </>
              )}

              <Grid item xs={6}>
                {renderTextField(
                  "firstName",
                  "first-name",
                  true,
                  false,
                  "text",
                  undefined,
                  undefined,
                  undefined,
                )}
              </Grid>

              <Grid item xs={6}>
                {renderTextField(
                  "lastName",
                  "surname",
                  true,
                  false,
                  "text",
                  undefined,
                  undefined,
                  undefined,
                )}
              </Grid>

              <Grid item xs={6}>
                {renderDatePicker("birthDate", "date-of-birth", true)}
              </Grid>

              <Grid item xs={6}>
                {renderTextField(
                  "motherName",
                  "mother-s-name",
                  true,
                  false,
                  "text",
                  undefined,
                  undefined,
                  undefined,
                )}
              </Grid>

              <Grid item xs={12}>
                {renderTextField(
                  "email",
                  "worker-email",
                  true,
                  false,
                  "text",
                  undefined,
                  undefined,
                  undefined,
                )}
              </Grid>
              {/* Campos comentados segundo pedido da acelen para a determinada sprint-60 */}
              <Grid item xs={6}>
                {renderTextField(
                  "phone",
                  "phone",
                  true,
                  false,
                  "text",
                  undefined,
                  undefined,
                  undefined,
                  watch("phone")?.length >= 6 && watch("phone")?.[5] === "9"
                    ? "(99) 99999-9999"
                    : "(99) 9999-9999",
                )}
              </Grid>
              {/* sprint-60 */}

              <Grid item xs={6}>
                {renderTextField(
                  "cep",
                  "cep",
                  true,
                  false,
                  "text",
                  undefined,
                  undefined,
                  undefined,
                  "99999-999",
                  isFetchingPostalCode,
                )}
              </Grid>

              <Grid item xs={6}>
                {renderTextField(
                  "addressNumber",
                  "number",
                  true,
                  false,
                  "text",
                  undefined,
                  undefined,
                  undefined,
                )}
              </Grid>

              <Grid item xs={6}>
                {renderTextField(
                  "city",
                  "city",
                  true,
                  false,
                  "text",
                  undefined,
                  undefined,
                  undefined,
                )}
              </Grid>

              <Grid item xs={6}>
                {renderAutocompleteField("state", "select-state-uf", ufOptions)}
              </Grid>

              <Grid item xs={12}>
                {renderTextField(
                  "address",
                  "address",
                  true,
                  false,
                  "text",
                  undefined,
                  undefined,
                  undefined,
                )}
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="h4"
                  color="rgba(0,0,0,0.75)"
                  marginTop="1rem"
                >
                  {t("header-form-worker")}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>

              <Grid item xs={6}>
                {renderToggleButtonGroup(
                  "routineStopWorker",
                  "work-stoppage-or-routine-worker",
                  dataPickListWorkShift?.values?.map((value) => ({
                    value: value,
                    label: value,
                  })),
                )}
              </Grid>
              <Grid item xs={6}></Grid>

              <Grid item xs={6}>
                {renderToggleButtonGroup(
                  "temporaryWorker",
                  "temporary-worker",
                  booleanOptions,
                )}
              </Grid>
              <Grid item xs={6}></Grid>

              {/* Campos comentados segundo pedido da acelen para a determinada sprint-60 */}
              <Grid item xs={6}>
                {renderToggleButtonGroup(
                  "workShift",
                  "work-shift",
                  dataPickListTurno?.values?.map((value) => ({
                    value: value,
                    label: value,
                  })),
                )}
              </Grid>
              <Grid item xs={6}></Grid>

              <Grid item xs={6} alignContent="end">
                {renderToggleButtonGroup(
                  "subcontractor",
                  "subcontractor",
                  booleanOptions,
                )}
              </Grid>
              <Grid item xs={6} alignContent="end" marginBottom="1rem">
                {renderTextField(
                  "subcontractorCompany",
                  "subcontractorCompany",
                  false,
                  watch("subcontractor") === "Yes" ? false : true,
                  "text",
                  true,
                )}{" "}
              </Grid>
              {/* sprint-60 */}

              <Grid item xs={12}>
                <Divider />
              </Grid>

              <Grid item xs={6}>
                {renderAutocompleteField(
                  "sowId",
                  "statement-of-work-id",
                  Sows?.map((option) => ({
                    value: option.sowID,
                    label: option.sowID,
                    startDate: option.startDate,
                    endDate: option.endDate,
                  })),
                )}
              </Grid>

              {/* Campos comentados segundo pedido da acelen para a determinada sprint-60 */}
              <Grid item xs={6}></Grid>
              <Grid item xs={6}>
                {renderAutocompleteField(
                  "department",
                  "department",
                  dataPickListDepartamento?.values?.map((value) => ({
                    value: value,
                    label: value,
                  })),
                  false,
                  !Array.isArray(dataPickListDepartamento?.values),
                  isLoadingPickListDepartamento,
                )}
              </Grid>
              {/* sprint-60 */}

              <Grid item xs={6}>
                {renderAutocompleteField(
                  "sowWorkerRole",
                  "sow-worker-role",
                  sowWorkerRoles?.map((option) => ({
                    value: option.name,
                    label: option.name,
                  })),
                )}
              </Grid>

              <Grid item xs={6}>
                {renderAutocompleteField(
                  "siteCode",
                  "site-code-site-code-star",
                  officeOptions,
                )}
              </Grid>

              <Grid item xs={6}>
                {renderAutocompleteField(
                  "location",
                  "location-code",
                  officeOptions,
                )}
              </Grid>

              <Grid item xs={6}>
                {renderDatePicker(
                  "startDate",
                  "start-date",
                  true,
                  watch("sowStartDate"),
                  startOfDay(watch("sowEndDate")),
                  !watch("sowId"),
                )}
              </Grid>

              <Grid item xs={6}>
                {renderDatePicker(
                  "endDate",
                  "end-date",
                  true,
                  startOfDay(
                    parse(watch("startDate"), "dd/MM/yyyy", new Date()),
                  ),
                  startOfDay(watch("sowEndDate")),
                  !watch("startDate"),
                )}
              </Grid>

              <Grid item xs={12}>
                {renderTextField(
                  "comment",
                  "comment",
                  false,
                  false,
                  "text",
                  true,
                  4,
                  4,
                )}
              </Grid>

              <Grid item xs={12}>
                <Grid container justifyContent="flex-end">
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isCreateWorker}
                  >
                    {isCreateWorker ? <CircularProgress /> : t("register")}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          )}
        </form>
      </Box>
    </Grid>
  );
};
